import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FIELD_SEARCH_FIELDS, FIELD_SEARCH_OPERATORS } from "../utils/adminFilters";

export const SAVED_VIEWS_STORAGE_KEY = "lead3a.admin.savedViews.v1";
const SavedViewsContext = createContext(null);
const SECTION_IDS = new Set(["complexes", "transfers", "sip-dispatcher", "prices"]);

function sanitizeSettings(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter(([, item]) => ["string", "number", "boolean"].includes(typeof item)));
}

function sanitizeFieldFilters(value, legacyFilters) {
  const source = Array.isArray(value)
    ? value
    : legacyFilters?.field
      ? [{
          field: legacyFilters.field,
          operator: legacyFilters.operator,
          value: legacyFilters.value,
        }]
      : [];

  return source.flatMap((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return [];
    if (!FIELD_SEARCH_FIELDS.includes(item.field)) return [];
    const operator = FIELD_SEARCH_OPERATORS.includes(item.operator) ? item.operator : "contains";
    return [{
      id: String(item.id || createId()),
      field: item.field,
      operator,
      value: item.value == null ? "" : String(item.value),
    }];
  });
}

function sanitizeFilters(value) {
  const filters = sanitizeSettings(value);
  return {
    ...filters,
    fieldFilters: sanitizeFieldFilters(value?.fieldFilters, value),
  };
}

function sanitizeView(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const id = String(value.id || "").trim();
  const name = String(value.name || "").trim();
  const sectionId = String(value.sectionId || "").trim();
  if (!id || !name || !SECTION_IDS.has(sectionId)) return null;
  return {
    id,
    name,
    sectionId,
    filters: sanitizeFilters(value.filters),
    search: typeof value.search === "string" ? value.search : "",
    sort: sanitizeSettings(value.sort),
    pageSize: value.pageSize == null
      ? null
      : Number.isFinite(Number(value.pageSize))
        ? Number(value.pageSize)
        : null,
    settings: sanitizeSettings(value.settings),
    createdAt: typeof value.createdAt === "string" ? value.createdAt : new Date().toISOString(),
    updatedAt: typeof value.updatedAt === "string" ? value.updatedAt : new Date().toISOString(),
  };
}

function readViews() {
  try {
    const value = JSON.parse(window.localStorage.getItem(SAVED_VIEWS_STORAGE_KEY) || "[]");
    return Array.isArray(value) ? value.map(sanitizeView).filter(Boolean) : [];
  } catch { return []; }
}

function createId() {
  return window.crypto?.randomUUID?.() || `view-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function SavedViewsProvider({ children }) {
  const [views, setViews] = useState(readViews);
  useEffect(() => {
    try { window.localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(views)); } catch {}
  }, [views]);

  const createView = useCallback((sectionId, name, state) => {
    const now = new Date().toISOString();
    const view = sanitizeView({ ...state, id: createId(), name, sectionId, createdAt: now, updatedAt: now });
    if (view) setViews((current) => [...current, view]);
    return view;
  }, []);
  const updateView = useCallback((id, state) => {
    let updated = null;
    setViews((current) => current.map((view) => {
      if (view.id !== id) return view;
      updated = sanitizeView({ ...view, ...state, id: view.id, name: view.name, sectionId: view.sectionId, createdAt: view.createdAt, updatedAt: new Date().toISOString() });
      return updated || view;
    }));
    return updated;
  }, []);
  const removeView = useCallback((id) => setViews((current) => current.filter((view) => view.id !== id)), []);
  const value = useMemo(() => ({ views, createView, updateView, removeView }), [createView, removeView, updateView, views]);
  return <SavedViewsContext.Provider value={value}>{children}</SavedViewsContext.Provider>;
}

export function useSavedViews() {
  const value = useContext(SavedViewsContext);
  if (!value) throw new Error("useSavedViews must be used inside SavedViewsProvider");
  return value;
}
